/**
 * Ресурсы работы в её карточке (ПЛ-13, РС-05): люди по профессиям, техника и материалы с количеством,
 * трудоёмкостью по норме или заданной, стоимость при уровне денег «Акты»; конкретные люди — только
 * с действующими допусками на даты работы.
 */
import {
  ASSIGNMENT_CURVE_TITLES,
  ASSIGNMENT_CURVES,
  type AssignmentCurve,
  type ResourceAssignmentRow,
  type ResourceRow,
  seesMoney,
  type WorkerRow,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { tenge } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { toast } from '../../ui/feedback';
import { Input, Select } from '../../ui/form';
import { LField } from './LField';

const num = (s: string) => {
  const v = Number(s.replace(/\s/g, '').replace(',', '.'));
  return s.trim() === '' || !Number.isFinite(v) ? null : v;
};

export function ActivityResources({
  scheduleId,
  activityId,
  canEdit,
  unit,
}: {
  scheduleId: string;
  activityId: string;
  canEdit: boolean;
  /** Единица объёма работы — для нормы на единицу */
  unit: string | null;
}) {
  const me = useMe();
  const money = seesMoney(me.access, 'acts');
  const qc = useQueryClient();
  const key = ['assignments', scheduleId];
  const asg = useQuery({
    queryKey: key,
    queryFn: () => api<ResourceAssignmentRow[]>(`/schedules/${scheduleId}/assignments`),
  });
  const res = useQuery({
    queryKey: ['resources'],
    queryFn: () => api<ResourceRow[]>('/resources', { query: { all: true } }),
    retry: false,
  });
  const people = useQuery({
    queryKey: ['activity-workers', activityId],
    queryFn: () => api<WorkerRow[]>(`/activities/${activityId}/workers`),
    retry: false,
  });
  const allWorkers = useQuery({
    queryKey: ['workers', 'all'],
    queryFn: () => api<WorkerRow[]>('/workers'),
    enabled: canEdit,
    retry: false,
  });
  const [f, setF] = useState({
    resourceId: '',
    units: '1',
    mode: 'units',
    amount: '',
    norm: '',
    curve: 'flat' as AssignmentCurve,
  });
  const [workerId, setWorkerId] = useState('');
  const put = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<ResourceAssignmentRow[]>(`/schedules/${scheduleId}/assignments`, { method: 'PUT', body }),
    onSuccess: (rows) => {
      qc.setQueryData(key, rows);
      qc.invalidateQueries({ queryKey: ['resource-load'] });
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`/assignments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ['resource-load'] });
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const person = useMutation({
    mutationFn: (x: { workerId: string; remove?: boolean }) =>
      api(`/activities/${activityId}/workers`, { body: x }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['activity-workers', activityId] });
      setWorkerId('');
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const mine = (asg.data ?? []).filter((a) => a.activityId === activityId);
  const resById = new Map((res.data ?? []).map((r) => [r.id, r]));
  const unitOf = (r?: ResourceRow) =>
    !r ? '' : r.kind === 'material' ? r.unit : r.kind === 'equipment' ? 'маш.-ч' : 'чел.-ч';
  const total = mine.reduce((s, a) => s + (a.cost ?? 0), 0);
  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-[14px] font-semibold">
        Ресурсы работы
        {money && mine.some((a) => a.cost != null) ? (
          <span className="ml-2 font-normal text-ink-2">стоимость {tenge(total)}</span>
        ) : null}
      </h3>
      {mine.length ? (
        <ul className="divide-y divide-line text-[14px]">
          {mine.map((a) => {
            const r = resById.get(a.resourceId);
            return (
              <li key={a.id} className="flex items-center justify-between gap-2 py-1.5">
                <span className="min-w-0">
                  <span className="block truncate">
                    {r?.name ?? 'ресурс'}
                    {r?.grade ? `, ${r.grade} р.` : ''}
                  </span>
                  <span className="text-[12px] text-ink-2">
                    {r?.kind === 'material' ? '' : `${a.units} одновременно · `}
                    {a.total.toLocaleString('ru-RU')} {unitOf(r)}
                    {a.normPerUnit != null ? ` (норма ${a.normPerUnit} на ${unit ?? 'ед.'})` : ''}
                    {money && a.cost != null ? ` · ${tenge(a.cost)}` : ''}
                    {a.curve && a.curve !== 'flat'
                      ? ` · ${ASSIGNMENT_CURVE_TITLES[a.curve].toLowerCase()}`
                      : ''}
                  </span>
                </span>
                {canEdit ? (
                  <IconButton
                    label="Убрать ресурс"
                    onClick={() => del.mutate(a.id)}
                    className="size-11 md:size-9"
                  >
                    <Trash2 className="size-4" />
                  </IconButton>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-[13px] text-ink-2">
          Ресурсы не назначены — потребность и стоимость по этой работе не считаются.
        </p>
      )}
      {canEdit && res.data ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          <LField label="Ресурс" className="col-span-2">
            <Select
              value={f.resourceId}
              onChange={(e) => setF({ ...f, resourceId: e.target.value })}
              className="max-md:h-11"
            >
              <option value="">Выберите…</option>
              {res.data
                .filter((r) => r.isActive)
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                    {r.grade ? `, ${r.grade} р.` : ''}
                  </option>
                ))}
            </Select>
          </LField>
          <LField label="Как считать">
            <Select
              value={f.mode}
              onChange={(e) => setF({ ...f, mode: e.target.value })}
              className="max-md:h-11"
            >
              <option value="units">
                {resById.get(f.resourceId)?.kind === 'material' ? 'Количество' : 'Сколько одновременно'}
              </option>
              <option value="norm">Норма на единицу объёма</option>
              <option value="amount">Всего на работу</option>
            </Select>
          </LField>
          {f.mode === 'units' ? (
            <LField
              label={
                resById.get(f.resourceId)?.kind === 'material'
                  ? `Количество, ${resById.get(f.resourceId)?.unit}`
                  : 'Людей или машин'
              }
            >
              <Input
                inputMode="decimal"
                value={f.units}
                onChange={(e) => setF({ ...f, units: e.target.value })}
                className="max-md:h-11"
              />
            </LField>
          ) : f.mode === 'norm' ? (
            <LField label={`На 1 ${unit ?? 'ед.'}`}>
              <Input
                inputMode="decimal"
                value={f.norm}
                onChange={(e) => setF({ ...f, norm: e.target.value })}
                className="max-md:h-11"
              />
            </LField>
          ) : (
            <LField label="Всего">
              <Input
                inputMode="decimal"
                value={f.amount}
                onChange={(e) => setF({ ...f, amount: e.target.value })}
                className="max-md:h-11"
              />
            </LField>
          )}
          <LField label="По длительности">
            <Select
              value={f.curve}
              onChange={(e) => setF({ ...f, curve: e.target.value as AssignmentCurve })}
              className="max-md:h-11"
            >
              {ASSIGNMENT_CURVES.map((c) => (
                <option key={c} value={c}>
                  {ASSIGNMENT_CURVE_TITLES[c]}
                </option>
              ))}
            </Select>
          </LField>
          <div className="flex items-end">
            <Button
              disabled={!f.resourceId}
              loading={put.isPending}
              onClick={() => {
                put.mutate({
                  activityId,
                  resourceId: f.resourceId,
                  units: num(f.units) ?? 1,
                  normPerUnit: f.mode === 'norm' ? num(f.norm) : null,
                  amount: f.mode === 'amount' ? num(f.amount) : null,
                  curve: f.curve,
                });
                setF({ ...f, resourceId: '' });
              }}
              className="max-md:h-11"
            >
              Добавить ресурс
            </Button>
          </div>
        </div>
      ) : null}
      {people.data ? (
        <div>
          <h4 className="mb-1 text-[13px] font-semibold">Люди на работе</h4>
          {people.data.length ? (
            <ul className="mb-2 flex flex-wrap gap-2">
              {people.data.map((w) => (
                <li
                  key={w.id}
                  className="inline-flex items-center gap-1 rounded-sm bg-surface-2 py-1 pr-1 pl-2 text-[13px]"
                >
                  {w.fullName}
                  {canEdit ? (
                    <IconButton
                      label={`Снять ${w.fullName}`}
                      onClick={() => person.mutate({ workerId: w.id, remove: true })}
                      className="size-11 md:size-7"
                    >
                      <Trash2 className="size-3.5" />
                    </IconButton>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mb-2 text-[13px] text-ink-2">Никто не назначен.</p>
          )}
          {canEdit && allWorkers.data ? (
            <div className="flex gap-2">
              <Select
                value={workerId}
                onChange={(e) => setWorkerId(e.target.value)}
                className="min-w-0 flex-1 max-md:h-11"
                aria-label="Назначить человека"
              >
                <option value="">Назначить человека…</option>
                {allWorkers.data
                  .filter((w) => w.status === 'active' && !people.data!.some((p) => p.id === w.id))
                  .map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.fullName}
                      {w.resourceName ? ` · ${w.resourceName}` : ''}
                      {w.expired.length || w.missing.length ? ' · документы!' : ''}
                    </option>
                  ))}
              </Select>
              <Button
                disabled={!workerId}
                onClick={() => person.mutate({ workerId })}
                className="max-md:h-11"
              >
                Назначить
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
